import {
  Armchair,
  LayoutDashboard,
  Users,
  ClipboardList,
  BarChart3,
  User,
  BookOpen,
  BadgeCheck,
  ScrollText,
  Building2,
  type LucideIcon,
} from "lucide-react";

import type { Role } from "@/contexts/auth-context";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * Story 16.2 — rendered non-interactive (a `· locked` span, not a link) for a
   * gated legacy clinician. Set only by {@link getNavForUser}; undefined/false
   * everywhere else, so normal nav is unaffected.
   */
  locked?: boolean;
}

// Clinician workspace nav (UX Screen 02 order). Resources is the legacy-clinician
// extra; destinations beyond this story land in later epics (placeholders for now).
const CLINICIAN_NAV: NavItem[] = [
  { label: "Workspace", to: "/workspace", icon: LayoutDashboard },
  { label: "Clients", to: "/clients", icon: Users },
  { label: "Evaluations", to: "/evaluations", icon: ClipboardList },
  { label: "Results", to: "/results", icon: BarChart3 },
  { label: "Profile", to: "/profile", icon: User },
  { label: "Resources", to: "/resources", icon: BookOpen },
];

// Admin nav (org_admin + super_admin) — every entry is backed by a shipped screen.
// The 2.9 scaffold also carried Readiness / Activity / Settings entries pointing at
// "Coming in Epic 14 / 15 / 11" placeholders; no FR, story or PRD ever specified those
// screens and the client-validated sidebar (Screens 10 / 20) has never shown them, so
// entries, routes and placeholders were removed together. Do not re-add a nav entry
// ahead of the screen it names.
// Profile is pinned last (matches the clinician nav + UX convention); super_admin-only
// items are spliced in BEFORE it — see SUPER_ADMIN_NAV / getNavForRole.
const ADMIN_NAV: NavItem[] = [
  { label: "Users", to: "/admin/users", icon: Users },
  { label: "Profile", to: "/profile", icon: User },
];

/**
 * Story 15.7 (D4 / AC16a) — the super_admin sidebar is SCOPED. Every one of the
 * seven client-validated admin mockups pairs an "Admin scope" toggle with its
 * own nav group, and the selector switches the LIST rather than merely a
 * highlight.
 */
export type AdminScope = "individual" | "organizations";

// Platform surfaces reachable by super_admin ONLY — org_admin is denied at the route
// guard and would 403 (route-access.ts SUPER_ADMIN_ROLES), so these live in their own
// slot and are NEVER appended to the shared ADMIN_NAV (Franklin#428: an org_admin must
// not see a link to a route that 403s).
//
// SCOPE: Individual clinicians — everything that administers PEOPLE.
//
// Certificate verification (16.4) and Audit log (11.7) have no mockup home
// because both were built after the mockups were drawn. They administer people,
// so they belong here; the alternative was a third scope for two entries.
const SUPER_ADMIN_INDIVIDUAL_NAV: NavItem[] = [
  // Story 15.7 — the operational console (Screen 10) is the super_admin's
  // Dashboard, the first entry in the mockup's Individual nav group, and the
  // post-sign-in landing (guards.tsx :: HomeRedirect). No entry existed here at
  // all before it: the sidebar had no home surface for a super_admin.
  { label: "Dashboard", to: "/admin/console", icon: LayoutDashboard },
  { label: "Users", to: "/admin/users", icon: Users },
  // Story 16.4's legacy-cert verification queue. The label mirrors the
  // LegacyCertQueueScreen H1 so the nav item, the page title, and the brand
  // sub-line (getSectionLabel) all read identically.
  { label: "Certificate verification", to: "/admin/legacy-certs", icon: BadgeCheck },
  // Story 11.7 — the audit console. An org_admin is DENIED query_audit_log in the
  // backend MATRIX, so a shared-nav entry would link them to a 403.
  { label: "Audit log", to: "/admin/audit", icon: ScrollText },
];

// SCOPE: Organizations — the enterprise estate.
//
// Story 15.6 — the platform organization directory (Screen 23).
// Capability.manage_organizations is ANY_ORG, so an org_admin linked here would
// collect a 403 — and the screen lists EVERY organization on the platform, which
// is precisely what an org_admin must never see. This is also the entry that
// makes /admin/orgs/new reachable without a deep link.
//
// The label is the mockup's bare "Directory" again. It had been lengthened to
// "Organization directory" for one stated reason — that no scope block existed
// to carry the qualifier, so an unqualified "Directory" would not say what it
// listed. Story 15.7 built the block, so the qualifier is now on the toggle
// above the list and the reason is spent.
const SUPER_ADMIN_ORGANIZATIONS_NAV: NavItem[] = [
  { label: "Directory", to: "/admin/orgs", icon: Building2 },
];

// Org-scoped self-service surfaces reachable by org_admin ONLY — super_admin is
// denied at the route guard (route-access.ts ORG_ADMIN_ONLY_ROLES, mirroring the
// backend MATRIX) so, per the same Franklin#428 posture, these are never appended
// to the shared ADMIN_NAV. Story 14.1's dashboard home (Screen 25) and Story
// 14.11's Seats & allocation (Screen 26) are its members, in the mockup's
// sidebar order (Screen 26 :29-32).
//
// These TWO are the whole org_admin sidebar: Screens 25 and 26 give it exactly
// these entries, and every shared ADMIN_NAV entry is hidden (see
// ORG_ADMIN_HIDDEN_NAV_PATHS) until an E11 screen exists that is scoped to ONE
// organization. Screen 25's sidebar note is the reason — "Operational state
// only — seat pool, calibration status as a state flag, subscription dates. PHI
// and clinical content stay inside each clinician's own workspace."
const ORG_ADMIN_NAV: NavItem[] = [
  { label: "Dashboard", to: "/org-admin/dashboard", icon: LayoutDashboard },
  { label: "Seats & allocation", to: "/org-admin/seats", icon: Armchair },
];

// Shared ADMIN_NAV entries the org_admin sidebar does NOT show — today just
// `/profile`, a clinician surface an org_admin has no professional details for
// (Story 14.9 / FR84 put their Settings in the account menu instead). Keyed by
// path, not label, for the LEGACY_LOCKED_PATHS reason: a label rename must not
// silently restore an entry. super_admin keeps every one.
//
// This list once also hid /admin/readiness, /admin/activity and /admin/settings.
// It no longer needs to: those entries, their routes and their placeholders were
// deleted outright as unspecified scaffold, so there is nothing left to hide.
// Franklin#428's posture — "an org_admin must not see a link to a route that
// 403s" — is what keeps this filter here for the entries that remain.
//
// Every ROUTE that still exists stays reachable for every authenticated role;
// this is navigation only. Re-list an entry the day a shared ADMIN_NAV screen
// gains a super_admin-only dimension an org_admin must not be linked to.
//
// `/admin/users` is deliberately NOT listed (Story 11.8, D-12): Screens 20 and 21
// open for an org_admin, scoped by the server to their own organization (11.1 AC6
// filters by the actor's own org claim; 11.3 refuses a cross-org target with
// ADMIN-007), and the platform numerics stay super_admin-only.
const ORG_ADMIN_HIDDEN_NAV_PATHS: ReadonlySet<string> = new Set(["/profile"]);

/**
 * The scope a path belongs to (Story 15.7 AC16a).
 *
 * The scope is DERIVED FROM THE PATH rather than held in state, because the
 * mockup's toggle is a pair of links and a link is what it should stay: with
 * state, the sidebar could offer the Organizations list while the operator is
 * looking at a Users screen, which is a sidebar disagreeing with its own page.
 *
 * Everything that is not the organization estate is Individual — including
 * `/profile` and every non-admin path, so a super_admin who wanders off the
 * admin tree still gets a coherent sidebar rather than an empty one.
 */
export function adminScopeForPath(pathname: string): AdminScope {
  return pathname === "/admin/orgs" || pathname.startsWith("/admin/orgs/")
    ? "organizations"
    : "individual";
}

/**
 * Roles are mutually exclusive (UX D4 — no role switcher). Returns the nav items the
 * current role may reach; role-hidden items are simply not produced (never rendered).
 *
 * super_admin sees the entries of ONE SCOPE (Story 15.7 AC16a) plus the shared
 * ADMIN_NAV's trailing Profile. Profile is pinned last and sits OUTSIDE both
 * scopes deliberately: it is the operator's own account, not an administered
 * object, so it is reachable whichever estate they are looking at.
 *
 * org_admin sees ORG_ADMIN_NAV plus whatever the ORG_ADMIN_HIDDEN_NAV_PATHS filter
 * leaves of the shared ADMIN_NAV (today: Users) — never the super_admin-only items
 * (they would 403), and never a scope toggle: they administer exactly one
 * organization, so there is no second estate to switch to. Everyone else gets the
 * clinician nav.
 *
 * `scope` defaults to Individual, which is the landing scope: `HomeRedirect`
 * sends a super_admin to `/admin/console`.
 */
export function getNavForRole(
  role: Role | string | undefined,
  scope: AdminScope = "individual",
): NavItem[] {
  if (role === "super_admin") {
    const scoped =
      scope === "organizations" ? SUPER_ADMIN_ORGANIZATIONS_NAV : SUPER_ADMIN_INDIVIDUAL_NAV;
    // `Users` no longer comes from ADMIN_NAV for a super_admin — it is an entry of
    // the Individual scope — so only the trailing Profile is taken from the shared
    // list, and it stays last.
    const profile = ADMIN_NAV.filter((item) => item.to === "/profile");
    return [...scoped, ...profile];
  }
  // Story 14.1 — org_admin's home (Dashboard) leads, then Seats & allocation,
  // then whatever the shared ADMIN_NAV filter leaves (today: Users). The filter —
  // rather than returning ORG_ADMIN_NAV outright — is what made Story 11.8's
  // un-listing of `/admin/users` a one-line change, and it stays for the same
  // reason: `/profile` is a clinician surface whose org_admin equivalent lives in
  // the account menu.
  if (role === "org_admin") {
    return [
      ...ORG_ADMIN_NAV,
      ...ADMIN_NAV.filter(
        (item) => !ORG_ADMIN_HIDDEN_NAV_PATHS.has(item.to),
      ),
    ];
  }
  return CLINICIAN_NAV;
}

// Story 16.2 — surfaces a gated legacy clinician may NOT reach during stage-1
// lockdown. Profile + Resources stay interactive (Decision 1 / FR80); these are
// rendered locked. Keyed by the route `to` path (not the display label) so a
// label rename can't silently drop the lock — the path is the stable identity
// the router and the backend allow-list both key on.
const LEGACY_LOCKED_PATHS: ReadonlySet<string> = new Set([
  "/workspace",
  "/clients",
  "/evaluations",
  "/results",
]);

/**
 * Nav for the current user. For a gated legacy clinician (Story 16.2) the
 * clinician nav is returned with Workspace/Clients/Evaluations/Results marked
 * `locked` (SidebarNav renders them non-interactive); Profile + Resources stay
 * live links. For everyone else this is exactly {@link getNavForRole}.
 */
export function getNavForUser(
  role: Role | string | undefined,
  isLegacyGated: boolean,
  scope: AdminScope = "individual",
): NavItem[] {
  const items = getNavForRole(role, scope);
  if (!isLegacyGated) return items;
  return items.map((item) =>
    LEGACY_LOCKED_PATHS.has(item.to) ? { ...item, locked: true } : item,
  );
}

/**
 * Active-section label for the brand sub-line (latest UX screens show the current
 * section under the wordmark). Matches the longest nav `to` that prefixes the
 * current path; returns undefined off-nav (e.g. /forbidden) so the brand can fall
 * back to its tagline.
 *
 * For a super_admin it searches BOTH scopes rather than the current one (Story
 * 15.7 AC16a). Scoping the search to the selected scope would blank the sub-line
 * for every admin path outside it — and the scope is derived from the path, so
 * the case is not hypothetical: it is what any deep link into the other estate
 * would produce.
 */
export function getSectionLabel(pathname: string, role: Role | string | undefined): string | undefined {
  const candidates =
    role === "super_admin"
      ? [...getNavForRole(role, "individual"), ...getNavForRole(role, "organizations")]
      : getNavForRole(role);
  const match = candidates
    .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.label;
}

// gh-625 — deliberately NO clinician entry: the clinician header renders no
// role pill at all (TopBar hides the slot; the state-aware SubscriptionPill is
// the clinician's access-state statement). Re-adding a clinician string here
// would still not render — the TopBar branch is the gate — but keep the map
// admin-only so the retired "Authorized for live scoring" copy cannot resurface.
export const ROLE_PILL_TEXT: Record<string, string> = {
  org_admin: "Org admin",
  super_admin: "Admin / owner",
};
